import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaTimes, FaHeartbeat, FaSpinner, FaCheck, FaQuestion, FaMinus,
  FaUser, FaEnvelope, FaPhone, FaBriefcase, FaMapMarkerAlt
} from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AnswerBadge = ({ v }) => {
  if (v === 'yes') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><FaCheck size={9} /> Yes</span>;
  if (v === 'no')  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200"><FaQuestion size={9} /> No</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200"><FaMinus size={9} /> N/A</span>;
};

/**
 * FinancialHealthCheckupModal
 *
 * Shows a mapped client's full Financial Health Check-Up submission to the
 * advisor — inputs (per-question Y/N/NA answers), computed score, risk band
 * and interpretation. Fetched from GET /api/advisor/client/:clientId/
 * financial-health-checkup.
 */
const FinancialHealthCheckupModal = ({ clientId, clientName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeSubmissionIdx, setActiveSubmissionIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/advisor/client/${clientId}/financial-health-checkup`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Unable to load check-up');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const submission = data?.history?.[activeSubmissionIdx];
  const summary = submission?.summary;

  const riskColors = {
    low:      { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', accent: 'text-emerald-600' },
    medium:   { chip: 'bg-amber-50 text-amber-700 border-amber-200',       accent: 'text-amber-600' },
    critical: { chip: 'bg-red-50 text-red-700 border-red-200',             accent: 'text-red-600' },
  };
  const rc = summary ? (riskColors[summary.risk_band] || riskColors.medium) : riskColors.medium;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="fhc-modal">
      <div
        className="bg-white rounded-2xl border border-[#EDE9DE] max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#EDE9DE] bg-gradient-to-r from-[#0F4C5C] to-[#093039]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#D4A84F] flex items-center justify-center">
              <FaHeartbeat className="text-[#263238]" size={20} />
            </div>
            <div>
              <p className="font-poppins text-white text-lg font-semibold leading-tight">Financial Health Check-Up</p>
              <p className="text-white/70 text-xs">{clientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="fhc-close">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 flex-1">
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#4E5B65]">
              <FaSpinner className="animate-spin mr-3" /> Loading submission…
            </div>
          )}
          {!loading && error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}
          {!loading && !error && !submission && (
            <div className="text-center py-12 text-[#78838C]">This client hasn&apos;t submitted the Financial Health Check-Up yet.</div>
          )}

          {!loading && !error && submission && (
            <>
              {/* Submission selector */}
              {data.history.length > 1 && (
                <div className="mb-5 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-[#78838C] mr-1">Submissions:</span>
                  {data.history.map((h, i) => (
                    <button
                      key={h.id}
                      onClick={() => setActiveSubmissionIdx(i)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        i === activeSubmissionIdx
                          ? 'bg-[#0F4C5C] text-white border-[#0F4C5C]'
                          : 'bg-white text-[#4E5B65] border-[#EDE9DE] hover:border-[#0F4C5C]/40'
                      }`}
                    >
                      {new Date(h.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </button>
                  ))}
                </div>
              )}

              {/* Client meta */}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaUser className="text-[#0F4C5C]" size={12} /> {data.client.name || '—'}
                </div>
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaEnvelope className="text-[#0F4C5C]" size={12} /> {data.client.email || '—'}
                </div>
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaPhone className="text-[#0F4C5C]" size={12} /> {data.client.phone || '—'}
                </div>
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaBriefcase className="text-[#0F4C5C]" size={12} /> {data.client.occupation || '—'}
                </div>
                {(data.client.city || data.client.state) && (
                  <div className="sm:col-span-2 p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                    <FaMapMarkerAlt className="text-[#0F4C5C]" size={12} /> {[data.client.city, data.client.state].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>

              {/* Score + Risk band */}
              <div className="grid sm:grid-cols-3 gap-3 mb-5">
                <div className="p-4 bg-white rounded-xl border border-[#EDE9DE] shadow-sm text-center">
                  <p className="text-xs text-[#78838C] uppercase tracking-wide">Score</p>
                  <p className="font-poppins text-3xl font-semibold text-[#0F4C5C]">{summary.score}<span className="text-base text-[#78838C]">/{summary.max_score}</span></p>
                  <p className="text-xs text-[#78838C] mt-1">{summary.score_percent}%</p>
                </div>
                <div className="p-4 bg-white rounded-xl border border-[#EDE9DE] shadow-sm text-center">
                  <p className="text-xs text-[#78838C] uppercase tracking-wide">Answers</p>
                  <p className="font-poppins text-2xl font-semibold text-[#0F4C5C]">
                    <span className="text-emerald-600">{summary.yes_count}Y</span>
                    <span className="mx-1 text-[#78838C]">·</span>
                    <span className="text-red-600">{summary.no_count}N</span>
                    <span className="mx-1 text-[#78838C]">·</span>
                    <span className="text-slate-500">{summary.na_count}NA</span>
                  </p>
                  <p className="text-xs text-[#78838C] mt-1">Total: {summary.answered}/{summary.total_questions}</p>
                </div>
                <div className={`p-4 rounded-xl border shadow-sm text-center ${rc.chip}`}>
                  <p className="text-xs uppercase tracking-wide opacity-80">Risk Band</p>
                  <p className="font-poppins text-lg font-semibold mt-1 leading-tight">{summary.risk_headline}</p>
                </div>
              </div>

              {/* Interested-in-review chip */}
              {submission.interested_in_review && (
                <div className="mb-5 p-3 rounded-xl bg-[#FBF6EA] border border-[#D4A84F]/40 text-[#846829] text-sm font-medium flex items-center gap-2">
                  <FaCheck size={12} /> Client has requested a <strong>detailed Financial Health Review using Arth Kundli</strong>.
                </div>
              )}

              {/* Interpretation */}
              <div className="mb-5 p-4 bg-[#F8F6F0] rounded-xl border border-[#EDE9DE]">
                <p className="text-xs uppercase tracking-wide text-[#78838C] mb-1">Interpretation</p>
                <p className="text-[#263238] text-sm leading-relaxed">{summary.interpretation}</p>
              </div>

              {summary.consequences?.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs uppercase tracking-wide text-[#78838C] mb-2">Consequences to Discuss</p>
                  <ul className="space-y-1.5">
                    {summary.consequences.map((c, i) => (
                      <li key={i} className={`text-sm flex items-start gap-2 ${rc.accent}`}>
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-current flex-shrink-0"></span>
                        <span className="text-[#263238]">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Per-question answers */}
              <div>
                <p className="text-xs uppercase tracking-wide text-[#78838C] mb-2">Per-question Answers</p>
                <div className="space-y-2">
                  {submission.detailed_answers.map((qa) => (
                    <div key={qa.number} className="p-3 rounded-lg bg-white border border-[#EDE9DE] flex items-start gap-3">
                      <span className="w-6 h-6 rounded-md bg-[#0F4C5C] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {qa.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#263238] leading-snug">{qa.text}</p>
                      </div>
                      <AnswerBadge v={qa.answer} />
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-4 text-xs text-[#78838C]">
                Submitted on {new Date(submission.created_at).toLocaleString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#EDE9DE] bg-[#F8F6F0] flex items-center justify-between">
          <p className="text-xs text-[#78838C]">Use this data to guide the detailed Financial Health Review discussion.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#0F4C5C] text-white text-sm font-semibold rounded-lg hover:bg-[#093039] transition-colors"
            data-testid="fhc-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialHealthCheckupModal;
